import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp } from 'lucide-react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { competitiveData, years } from './BenchmarkSnapshot';
import { chartBlue, chartGreen, chartRed } from '@/utils/chartColors';

// Transform data for Recharts
const chartData = years.map((year, index) => ({
  year,
  USA: competitiveData.usa.historicalScores[index],
  Mexico: competitiveData.mexico.historicalScores[index],
  Canada: competitiveData.canada.historicalScores[index],
}));

// Custom Tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  
  return (
    <div className="bg-card border border-border rounded-lg shadow-lg p-3">
      <p className="font-semibold text-foreground mb-2">{label}</p>
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center gap-2 text-sm">
          <div 
            className="w-3 h-3 rounded-full" 
            style={{ backgroundColor: entry.color }} 
          />
          <span className="text-muted-foreground">{entry.name}:</span>
          <span className="font-medium text-foreground">{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

// SECTION 2: 4-Year Brand Evolution Chart
export const EvolutionChart = () => {
  const blue = chartBlue();
  const green = chartGreen();
  const red = chartRed();
  return (
    <section className="mb-10" data-testid="brand-evolution-section">
      <div className="flex items-center gap-3 mb-6">
        <TrendingUp className="w-6 h-6 text-stable" />
        <h2 className="text-lg font-semibold text-foreground tracking-tight">4-Year Brand Evolution</h2>
      </div>
      
      <Card className="bg-card border-border/50">
        <CardContent className="p-6">
          <div className="w-full h-80" data-testid="evolution-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid 
                  strokeDasharray="3 3" 
                  stroke="hsl(var(--border))" 
                  opacity={0.3}
                />
                <XAxis 
                  dataKey="year" 
                  stroke="hsl(var(--muted-foreground))"
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                />
                <YAxis 
                  domain={[20, 80]}
                  stroke="hsl(var(--muted-foreground))"
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                  label={{ 
                    value: 'Brand Score', 
                    angle: -90, 
                    position: 'insideLeft',
                    fill: 'hsl(var(--muted-foreground))',
                    fontSize: 12
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="circle"
                />
                <Line 
                  type="monotone" 
                  dataKey="USA" 
                  stroke={blue} 
                  strokeWidth={3}
                  dot={{ fill: blue, strokeWidth: 2, r: 6 }}
                  activeDot={{ r: 8 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="Mexico" 
                  stroke={green} 
                  strokeWidth={2}
                  dot={{ fill: green, strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 7 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="Canada" 
                  stroke={red} 
                  strokeWidth={2}
                  dot={{ fill: red, strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 7 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          
          {/* Bottom legend with flags */}
          <div className="mt-4 pt-4 border-t border-border/50 flex flex-wrap items-center justify-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-1 rounded" style={{ backgroundColor: blue }} />
              <span className="text-sm text-muted-foreground">United States</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-1 rounded" style={{ backgroundColor: green }} />
              <span className="text-sm text-muted-foreground">Mexico</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-1 rounded" style={{ backgroundColor: red }} />
              <span className="text-sm text-muted-foreground">Canada</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
};

export default EvolutionChart;
